import React from "react";

const ReportsSkeletonLoader: React.FC = () => {
  return (
    <div className="h-full bg-white rounded-lg">
      <div className="max-w-6xl mx-auto min-h-full px-4">
        {/* Reports Header Skeleton */}
        <div className="w-full">
          <div className="bg-white px-4 py-3 flex justify-between shadow-md">
            {/* Reports Title */}
            <div className="text-gray-800 font-semibold text-lg">
              <div className="w-20 h-6 bg-gray-200 rounded animate-pulse"></div>
            </div>

            {/* Right side - Create buttons and Profile Icon */}
            <div className="flex items-center gap-4">
              <div className="flex items-center text-gray-400 py-2 px-3 gap-2 border border-gray-200 rounded-lg">
                <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
              </div>
              <div className="flex items-center text-white bg-gray-800 py-2 px-3 gap-2 border border-gray-200 rounded-lg">
                <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
              </div>
              <div className="w-[1px] h-6 bg-gray-200"></div>
              <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>

        <div className="overflow-auto">
          {/* Search Bar, Filter Button, and View Mode Buttons */}
          <div className="flex gap-2 items-center mt-4 mb-2">
            <div className="relative flex-1 p-1">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-full pl-10 pr-4 h-10 border border-gray-200 rounded-lg bg-gray-100 animate-pulse"></div>
            </div>
            {/* View Mode Buttons */}
            <div className="flex gap-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg border bg-gray-50 border-gray-200">
                <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
              </div>
              <div className="flex items-center justify-center w-10 h-10 rounded-lg border bg-blue-50 border-blue-200">
                <div className="w-4 h-4 bg-blue-200 rounded animate-pulse"></div>
              </div>
            </div>
            {/* Filter Button */}
            <div className="relative">
              <div className="flex items-center gap-2 w-10 h-10 justify-center rounded-lg border bg-gray-50 border-gray-200">
                <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Number of results text */}
          <div className="my-4 text-lg text-gray-600 font-semibold">
            <div className="w-40 h-6 bg-gray-200 rounded animate-pulse"></div>
          </div>

          {/* Table View Skeleton */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-8">
            {/* Table Header */}
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
                <div className="col-span-6">
                  <div className="w-28 h-4 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="col-span-3">
                  <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="col-span-2">
                  <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="col-span-1"></div>
              </div>
            </div>

            {/* Table Body */}
            <div className="space-y-0">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="px-6 py-2 border-b border-gray-100">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    {/* Template Name */}
                    <div className="col-span-6">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                        <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                      </div>
                    </div>

                    {/* Sharing */}
                    <div className="col-span-3">
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-gray-200 rounded animate-pulse"></div>
                        <div className="h-3 bg-gray-200 rounded w-20 animate-pulse"></div>
                      </div>
                    </div>

                    {/* Schedule */}
                    <div className="col-span-2">
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                        <div className="h-3 bg-gray-200 rounded w-12 animate-pulse"></div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="col-span-1 flex justify-end">
                      <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Report Editor Skeleton Loader
export const ReportEditorSkeletonLoader: React.FC = () => {
  return (
    <div className="h-full bg-white flex flex-col rounded-lg overflow-hidden">
      {/* Fixed Headers */}
      <div className="flex-shrink-0">
        {/* Reports Header */}
        <div className="relative">
          <div className="bg-white px-4 py-3 flex justify-between shadow-md">
            <div className="text-gray-800 font-semibold text-lg">
              <div className="w-20 h-6 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center text-gray-400 py-2 px-3 gap-2 border border-gray-200 rounded-lg">
                <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
              </div>
              <div className="flex items-center text-white bg-gray-800 py-2 px-3 gap-2 border border-gray-200 rounded-lg">
                <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
              </div>
              <div className="w-[1px] h-6 bg-gray-200"></div>
              <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
        {/* Workspace Header */}
        <div className="h-12 border-b bg-white">
          <div className="flex items-center justify-between px-4 py-2">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-5 bg-gray-200 rounded w-32 animate-pulse"></div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Editor Area */}
      <div className="flex-1 overflow-auto relative bg-gray-50">
        <div className="max-w-4xl mx-auto p-6">
          {/* Editor Content Skeleton */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="space-y-4">
              {/* Title */}
              <div className="h-8 bg-gray-200 rounded w-1/3 animate-pulse"></div>

              {/* Content paragraphs */}
              {[...Array(5)].map((_, i) => (
                <div key={i} className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/5 animate-pulse"></div>
                </div>
              ))}

              {/* Table */}
              <div className="mt-6">
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                    <div className="grid grid-cols-4 gap-4">
                      {[...Array(4)].map((_, i) => (
                        <div
                          key={i}
                          className="h-4 bg-gray-200 rounded animate-pulse"
                        ></div>
                      ))}
                    </div>
                  </div>
                  <div className="p-4">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="grid grid-cols-4 gap-4 mb-2">
                        {[...Array(4)].map((_, j) => (
                          <div
                            key={j}
                            className="h-3 bg-gray-200 rounded animate-pulse"
                          ></div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Report Template Editor Skeleton Loader
export const ReportTemplateEditorSkeletonLoader: React.FC = () => {
  return (
    <div className="h-full overflow-auto bg-gradient-to-br from-gray-50 via-white to-gray-50 rounded-lg">
      {/* Reports Header */}
      <div className="h-[72px] w-full border-b bg-white">
        <div className="px-4 py-3 flex justify-between">
          <div className="text-gray-800 font-semibold text-lg">
            <div className="w-20 h-6 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center text-gray-400 py-2 px-3 gap-2 border border-gray-200 rounded-lg">
              <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="flex items-center text-white bg-gray-800 py-2 px-3 gap-2 border border-gray-200 rounded-lg">
              <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
            </div>
            <div className="w-[1px] h-6 bg-gray-200"></div>
            <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Workspace Header */}
      <div className="h-[72px] w-full border-b bg-white">
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-5 bg-gray-200 rounded w-32 animate-pulse"></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
            <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Editor Content */}
      <div className="h-[calc(100%-144px)] w-full relative bg-gray-50">
        <div className="max-w-4xl mx-auto p-6">
          {/* Template Editor Content Skeleton */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="space-y-4">
              {/* Template Title */}
              <div className="h-8 bg-gray-200 rounded w-1/2 animate-pulse"></div>

              {/* Template Description */}
              <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse"></div>

              {/* Template Content */}
              {[...Array(6)].map((_, i) => (
                <div key={i} className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/5 animate-pulse"></div>
                </div>
              ))}

              {/* Template Structure */}
              <div className="mt-6">
                <div className="h-6 bg-gray-200 rounded w-32 animate-pulse mb-3"></div>
                <div className="space-y-2">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded w-48 animate-pulse"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsSkeletonLoader;
